import type { InvoiceDirection, InvoiceStatus } from '@tandemone/types'

import { cn } from '@/lib/utils'

const STATUS_STYLES: Record<
  InvoiceStatus,
  { className: string; label: string }
> = {
  draft: {
    className: 'bg-cream text-ink-soft ring-border',
    label: 'Draft'
  },
  sent: {
    className:
      'bg-accent-light text-accent ring-accent/15 dark:text-accent-mid',
    label: 'Sent'
  },
  paid: {
    className:
      'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/60',
    label: 'Paid'
  },
  cancelled: {
    className:
      'bg-ink-soft/10 text-ink-soft ring-border dark:bg-ink-soft/15 dark:text-ink-soft',
    label: 'Cancelled'
  }
}

const OVERDUE_STYLE = {
  className:
    'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900/60',
  label: 'Overdue'
}

/** the persisted status reads from the issuer's POV ('sent'). on the received
 * side that same row means "you owe this", so relabel sent → Unpaid. overdue
 * (derived) is shown in place of the sent/unpaid pill either way.
 */
export function InvoiceStatusBadge({
  status,
  isOverdue = false,
  direction = 'sent'
}: {
  status: InvoiceStatus
  isOverdue?: boolean
  direction?: InvoiceDirection
}) {
  if (isOverdue && status === 'sent') {
    return <Pill {...OVERDUE_STYLE} />
  }
  const base = STATUS_STYLES[status]
  const label =
    direction === 'received' && status === 'sent' ? 'Unpaid' : base.label
  return <Pill className={base.className} label={label} />
}

function Pill({ className, label }: { className: string; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
        className
      )}
    >
      {label}
    </span>
  )
}
